'use client';

import { useEffect } from 'react';
import { ThemeProvider as NextThemesProvider, useTheme } from 'next-themes';
import {
  THEME_CHOICE_COOKIE,
  THEME_RESOLVED_COOKIE,
  THEME_STORAGE_KEY,
  parseResolvedTheme,
  parseThemeChoice,
  readStoredThemeChoice,
  serialiseThemeCookie,
  serialiseThemeCookieRemoval,
  type ThemeChoice
} from '@/lib/utils/theme';
import { ThemePreferenceSync } from './theme-preference-sync';

/**
 * Keep the theme cookies in step with `next-themes`, which persists only to
 * `localStorage`. This is the mirror the whole no-flash design depends on: the
 * cookie is what the SERVER reads on the next request to paint the right theme
 * before any JavaScript runs.
 *
 * Renders nothing. It is a component rather than a hook inside the provider
 * because it has to sit *inside* `NextThemesProvider` to call `useTheme()`.
 *
 * `active` is false on the public (`forceLight`) tree, which never lets a user
 * pick a theme: there, any cookie left by an earlier authenticated session on
 * this browser is cleared instead, so the next public-page load paints light on
 * the first frame rather than flashing dark and correcting.
 *
 * **It mirrors a STORED choice, never the effective theme** (VALE-1164). Those
 * differ on exactly the browsers that matter: with nothing in `localStorage`,
 * `useTheme().theme` reports `defaultTheme`, so mirroring it would stamp a
 * `vale-theme-v2` cookie recording a decision nobody made — and the cookie is
 * precisely what `ThemePreferenceSync` reads back as "a choice was made on this
 * browser". A fresh browser is therefore left with no cookie at all, which is
 * the honest state and paints light exactly as it did before.
 */
function ThemeCookieMirror({ active }: { active: boolean }) {
  const { theme, resolvedTheme } = useTheme();

  useEffect(() => {
    if (!active) {
      document.cookie = serialiseThemeCookieRemoval(THEME_CHOICE_COOKIE);
      document.cookie = serialiseThemeCookieRemoval(THEME_RESOLVED_COOKIE);
      return;
    }

    const choice = readStoredThemeChoice();
    const resolved = parseResolvedTheme(resolvedTheme);
    // Mirror only a PERSISTED choice, and only once `next-themes` state agrees
    // with it. `setTheme` writes both together, so the two disagree in exactly
    // one situation — nothing is stored and `theme` is reporting `defaultTheme`
    // — which is the situation that must not produce a cookie.
    if (!choice || !resolved || choice !== parseThemeChoice(theme)) return;

    const secure = window.location.protocol === 'https:';
    document.cookie = serialiseThemeCookie(THEME_CHOICE_COOKIE, choice, {
      secure
    });
    document.cookie = serialiseThemeCookie(THEME_RESOLVED_COOKIE, resolved, {
      secure
    });
  }, [active, theme, resolvedTheme]);

  return null;
}

export interface ValeThemeProviderProps {
  children: React.ReactNode;
  /**
   * The choice the SERVER read from the cookie for this request, or `undefined`
   * when there was none. Passed down rather than re-read on the client so the
   * value is identical across SSR and hydration — reading `document.cookie`
   * during render would make the provider's own props differ between the two.
   */
  initialChoice?: ThemeChoice;
  /** The CSP nonce, so the anti-FOUC script survives `strict-dynamic` in prod. */
  nonce?: string;
  /**
   * Force light unconditionally, whatever the cookie says. Used for the public
   * (unauthenticated) tree, which is out of scope for the dark theme and has no
   * profile query to seed a preference from.
   */
  forceLight?: boolean;
}

/**
 * Vale's `next-themes` mount (VALE-520 · DARK-3; unconditional since the
 * `dark-theme` flag retired — VALE-871 · FLAG-6) — the cookie mirror and the
 * anti-FOUC nonce, in one place.
 *
 * There was no ThemeProvider anywhere in the app before this; `ui/sonner.tsx`
 * had been calling `useTheme()` against nothing since it was added.
 */
export function ValeThemeProvider({
  children,
  initialChoice,
  nonce,
  forceLight = false
}: ValeThemeProviderProps) {
  return (
    <NextThemesProvider
      attribute="class"
      enableSystem
      // The stored choice when we have one, so a browser with `localStorage`
      // unavailable or cleared still lands on the theme the cookie recorded
      // instead of falling back to a default. Otherwise LIGHT, on the public
      // tree and for a genuinely fresh browser alike — the product default
      // (VALE-644). `dark` and `system` are opt-ins a user reaches by opening
      // the menu, never somewhere their OS puts them.
      defaultTheme={initialChoice ?? 'light'}
      storageKey={THEME_STORAGE_KEY}
      forcedTheme={forceLight ? 'light' : undefined}
      // Sets `color-scheme` on `<html>`, so native scrollbars and form controls
      // follow the theme. Without it a dark page keeps a light scrollbar down
      // its edge, which reads as a rendering bug rather than a browser default.
      enableColorScheme
      disableTransitionOnChange
      nonce={nonce}
    >
      <ThemeCookieMirror active={!forceLight} />
      {/* Not on the public tree (VALE-548): it subscribes to `useProfileQuery`,
          which would fire an AUTHENTICATED `/profile` fetch for a reader with no
          session — the 401-then-federated-logout trap `providers-wrapper` warns
          about. */}
      {!forceLight && <ThemePreferenceSync initialChoice={initialChoice} />}
      {children}
    </NextThemesProvider>
  );
}
